import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "../config/api";
import axios from "axios";
import { parseBlobError } from "../utils/parseBlobError";
import {
  Calendar, Download, Loader2,
  ChevronLeft, Filter, TrendingUp, FileText
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { LOGO_URL } from "../config/constants";
import QuotesFunnelView from "../components/QuotesFunnelView";

const REPORT_TYPES = [
  { id: "deadline-compliance", label: "Deadline Compliance", icon: Calendar, color: "text-blue-400" },
  // BM-H76 — Rebekah's request: weekly matter-file opens per fee earner since
  // CaseMinder went live, so she can see her instructed / converting trend.
  { id: "matters-opened-weekly", label: "Matters Opened (weekly)", icon: TrendingUp, color: "text-teal" },
  // BM-H77 Phase-2 — Quotes funnel: sent → received → accepted → converted.
  // Weekly grid + per-fee-earner breakdown; embedded QuotesFunnelView owns
  // the fetch so this page stays a slim shell.
  { id: "quotes-funnel", label: "Quotes funnel", icon: FileText, color: "text-status-amber" },
];

export default function Reports() {
  const navigate = useNavigate();
  const [activeReport, setActiveReport] = useState("deadline-compliance");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [feeEarner, setFeeEarner] = useState("");

  const userId = sessionStorage.getItem("bonsai_user_id");
  const headers = { "X-Bonsai-User-Id": userId };

  const fetchReport = useCallback(async () => {
    // BM-H77 Phase-2: the Quotes funnel view is embedded and self-fetches
    // (see QuotesFunnelView.jsx). Skip the generic report fetch to avoid
    // hitting a route that doesn't apply.
    if (activeReport === "quotes-funnel") { setLoading(false); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      // BM-H76 — the weekly-opens report also honours the fee-earner filter,
      // so Rebekah can pick her own name and see just her trend.
      if (feeEarner && (activeReport === "matter-status" || activeReport === "matters-opened-weekly")) {
        params.set("fee_earner", feeEarner);
      }
      const resp = await axios.get(`${API}/hub/reports/${activeReport}?${params}`, { headers });
      setData(resp.data);
    } catch (err) {
      toast.error("Failed to load report");
    } finally {
      setLoading(false);
    }
  }, [activeReport, dateFrom, dateTo, feeEarner]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const exportCSV = async () => {
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (feeEarner) params.set("fee_earner", feeEarner);
      const resp = await axios.get(`${API}/hub/reports/export/${activeReport}?${params}`, {
        headers, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `caseminder_${activeReport}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Report exported");
    } catch (err) {
      const detail = await parseBlobError(err);
      toast.error(`Export failed: ${detail}`);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F0]">
      <nav className="glass-nav sticky top-0 z-50 w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Bonsai Law" className="w-10 h-10" />
              <div>
                <h1 className="text-lg font-bold text-teal">Reports</h1>
                <p className="text-[11px] text-txt-muted tracking-wide font-medium">CaseMinder Hub</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => navigate("/")}
                className="rounded-md text-txt-muted border-navy-border hover:bg-navy-hover" data-testid="back-to-dashboard">
                <ChevronLeft className="w-4 h-4 mr-1" /> Dashboard
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Report Type Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {REPORT_TYPES.map(({ id, label, icon: Icon, color }) => (
            <Button key={id} variant={activeReport === id ? "default" : "outline"} size="sm"
              onClick={() => { setActiveReport(id); setData(null); }}
              className={activeReport === id ? "bg-teal text-white" : `text-txt-muted border-navy-border`}
              data-testid={`report-tab-${id}`}>
              <Icon className={`w-4 h-4 mr-1.5 ${activeReport === id ? "" : color}`} />
              {label}
            </Button>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6 items-end">
          <div>
            <label className="text-[10px] text-txt-muted uppercase tracking-wider block mb-1">From</label>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="bg-navy border-navy-border text-txt text-xs w-40" data-testid="filter-from" />
          </div>
          <div>
            <label className="text-[10px] text-txt-muted uppercase tracking-wider block mb-1">To</label>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="bg-navy border-navy-border text-txt text-xs w-40" data-testid="filter-to" />
          </div>
          <Button size="sm" onClick={fetchReport} className="bg-teal text-white" data-testid="apply-filters">
            <Filter className="w-4 h-4 mr-1" /> Apply
          </Button>
          <Button size="sm" variant="outline" onClick={exportCSV} className="text-txt-muted border-navy-border gap-1.5" data-testid="export-csv">
            <Download className="w-4 h-4" /> Export CSV
          </Button>
        </div>

        {/* BM-H76 — fee-earner filter surfaces for reports that honour it. */}
        {(activeReport === "matter-status" || activeReport === "matters-opened-weekly" || activeReport === "quotes-funnel") && (
          <div className="flex flex-wrap gap-3 mb-6 items-end">
            <div>
              <label className="text-[10px] text-txt-muted uppercase tracking-wider block mb-1">Fee Earner</label>
              <Input
                type="text"
                placeholder="e.g. Rebekah Brake Manning or RM"
                value={feeEarner}
                onChange={e => setFeeEarner(e.target.value)}
                className="bg-navy border-navy-border text-txt text-xs w-64"
                data-testid="filter-fee-earner"
              />
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16 text-txt-muted gap-2">
            <Loader2 className="w-5 h-5 animate-spin" /><span>Loading report...</span>
          </div>
        ) : activeReport === "quotes-funnel" ? (
          <QuotesFunnelView dateFrom={dateFrom} dateTo={dateTo} feeEarner={feeEarner} />
        ) : data && (
          <div className="space-y-4">
            {activeReport === "deadline-compliance" && <ComplianceView data={data} />}
            {activeReport === "matters-opened-weekly" && <MattersOpenedWeeklyView data={data} />}
          </div>
        )}
      </main>
    </div>
  );
}

function ComplianceView({ data }) {
  const s = data.summary || {};
  const rate = s.compliance_rate || 0;
  const rateColor = rate >= 90 ? "text-teal" : rate >= 70 ? "text-status-amber" : "text-status-red";
  return (
    <div data-testid="report-compliance">
      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        <div className="glass-card rounded-md p-4 text-center">
          <p className={`text-3xl font-bold ${rateColor}`}>{rate}%</p>
          <p className="text-[10px] text-txt-muted uppercase tracking-wider mt-1">Compliance Rate</p>
        </div>
        <div className="glass-card rounded-md p-4 text-center">
          <p className="text-2xl font-bold text-white">{s.total}</p>
          <p className="text-[10px] text-txt-muted uppercase tracking-wider mt-1">Total</p>
        </div>
        <div className="glass-card rounded-md p-4 text-center">
          <p className="text-2xl font-bold text-teal">{s.completed}</p>
          <p className="text-[10px] text-txt-muted uppercase tracking-wider mt-1">Completed</p>
        </div>
        <div className="glass-card rounded-md p-4 text-center">
          <p className="text-2xl font-bold text-status-red">{s.overdue}</p>
          <p className="text-[10px] text-txt-muted uppercase tracking-wider mt-1">Overdue</p>
        </div>
        <div className="glass-card rounded-md p-4 text-center">
          <p className="text-2xl font-bold text-status-amber">{s.pending}</p>
          <p className="text-[10px] text-txt-muted uppercase tracking-wider mt-1">Pending</p>
        </div>
      </div>

      {/* By Category */}
      {data.by_category?.length > 0 && (
        <div className="glass-card rounded-md p-4 mb-4">
          <h4 className="text-xs font-bold text-txt-muted uppercase tracking-wider mb-3">By Category</h4>
          <div className="space-y-2">
            {data.by_category.map((cat, i) => (
              <div key={cat.category} className="flex items-center gap-3 text-xs">
                <span className="text-txt-muted w-32 truncate capitalize">{cat.category.replace(/_/g, " ")}</span>
                <span className="text-white font-bold w-8 text-right">{cat.total}</span>
                <div className="flex gap-2 text-[10px]">
                  {cat.completed > 0 && <span className="text-teal">{cat.completed} done</span>}
                  {cat.overdue > 0 && <span className="text-status-red">{cat.overdue} overdue</span>}
                  {cat.pending > 0 && <span className="text-status-amber">{cat.pending} pending</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* By Assignee (deadline compliance) */}
      {data.by_assignee?.length > 0 && (
        <div className="glass-card rounded-md p-4">
          <h4 className="text-xs font-bold text-txt-muted uppercase tracking-wider mb-3">By Assignee</h4>
          <div className="space-y-2">
            {data.by_assignee.map((a, i) => (
              <div key={a.assignee} className="flex items-center gap-3 text-xs">
                <span className="text-txt-muted w-40 truncate">{a.assignee}</span>
                <span className="text-white font-bold w-8 text-right">{a.total}</span>
                <div className="flex gap-2 text-[10px]">
                  {a.completed > 0 && <span className="text-teal">{a.completed} done</span>}
                  {a.overdue > 0 && <span className="text-status-red">{a.overdue} overdue</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// BM-H76 — Rebekah's weekly matter-opens view.
// Layout: summary cards → fee-earner totals → weekly table with mini bars so
// the trend reads at a glance. Weeks are ISO (Mon–Sun) formatted YYYY-Www.
function MattersOpenedWeeklyView({ data }) {
  const weeks = Array.isArray(data.weeks) ? data.weeks : [];
  const feTotals = Array.isArray(data.fee_earner_totals) ? data.fee_earner_totals : [];
  const total = data.total_matters || 0;
  const weeksTracked = weeks.length;
  const topFe = feTotals[0];
  const feFilter = data.filters?.fee_earner_resolved || data.filters?.fee_earner;

  // Scale bars against the busiest week so a quiet week doesn't look empty.
  const maxWeek = weeks.reduce((m, w) => Math.max(m, w.total || 0), 0) || 1;

  const avgPerWeek = weeksTracked ? (total / weeksTracked).toFixed(1) : "0";

  return (
    <div data-testid="report-matters-opened-weekly">
      {/* Summary */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div className="glass-card rounded-md p-4 text-center">
          <p className="text-3xl font-bold text-teal">{total}</p>
          <p className="text-[10px] text-txt-muted uppercase tracking-wider mt-1">
            Total Matters Opened{feFilter ? ` — ${feFilter}` : ""}
          </p>
        </div>
        <div className="glass-card rounded-md p-4 text-center">
          <p className="text-2xl font-bold text-white">{weeksTracked}</p>
          <p className="text-[10px] text-txt-muted uppercase tracking-wider mt-1">Weeks Tracked</p>
        </div>
        <div className="glass-card rounded-md p-4 text-center">
          <p className="text-2xl font-bold text-white">{avgPerWeek}</p>
          <p className="text-[10px] text-txt-muted uppercase tracking-wider mt-1">Avg / Week</p>
        </div>
        <div className="glass-card rounded-md p-4 text-center">
          <p className="text-lg font-bold text-teal truncate" title={topFe?.fee_earner || "—"}>
            {topFe?.fee_earner || "—"}
          </p>
          <p className="text-[10px] text-txt-muted uppercase tracking-wider mt-1">
            Top Fee Earner ({topFe?.total || 0})
          </p>
        </div>
      </div>

      {/* Per fee earner totals — hidden when filtering to one (redundant). */}
      {!feFilter && feTotals.length > 0 && (
        <div className="glass-card rounded-md p-4 mb-4" data-testid="report-matters-opened-weekly-by-fe">
          <h4 className="text-xs font-bold text-txt-muted uppercase tracking-wider mb-3">By Fee Earner</h4>
          <div className="space-y-2">
            {feTotals.map(fe => (
              <div key={fe.fee_earner} className="flex items-center gap-3 text-xs">
                <span className="text-txt-muted w-48 truncate">{fe.fee_earner}</span>
                <span className="text-white font-bold w-8 text-right">{fe.total}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Weekly breakdown */}
      {weeks.length > 0 ? (
        <div className="glass-card rounded-md p-4" data-testid="report-matters-opened-weekly-weeks">
          <h4 className="text-xs font-bold text-txt-muted uppercase tracking-wider mb-3">Weekly Breakdown</h4>
          <div className="space-y-2">
            {weeks.map(wk => {
              const pct = Math.round(((wk.total || 0) / maxWeek) * 100);
              return (
                <div key={wk.week} className="flex items-center gap-3 text-xs">
                  <span className="text-txt-muted w-24 shrink-0">{wk.week}</span>
                  <div className="flex-1 bg-navy-border/40 rounded-sm h-4 overflow-hidden min-w-0">
                    <div className="bg-teal h-4 rounded-sm" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-white font-bold w-8 text-right">{wk.total}</span>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="glass-card rounded-md p-8 text-center text-xs text-txt-muted">
          No matters opened in the selected range{feFilter ? ` for ${feFilter}` : ""}.
        </div>
      )}
    </div>
  );
}
